import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Snackbar,
} from '@material-ui/core';
import * as React from 'react';
import { DelayedLinearProgress } from 'components';
import { SettingsService } from 'services';
import { Setting } from 'interfaces';

/**
 * General reservation tunables stored as rows in the `settings` table.
 * Edits PUT each row via the existing SettingsService.editSetting.
 * Opening hours are NOT here: they live under Settings > Shops > To Go
 * because the same opening_hours table already drives other parts of
 * the app.
 */

const FIELDS: {
  key: string;
  label: string;
  type: 'number' | 'text' | 'email';
  helper?: string;
}[] = [
  {
    key: 'reservation_prior_notice_minutes',
    label: 'Prior notice (minutes)',
    type: 'number',
    helper: 'Minimum lead time before the booked slot.',
  },
  {
    key: 'reservation_book_ahead_days',
    label: 'Book ahead limit (days)',
    type: 'number',
    helper: 'How far in the future customers can book.',
  },
  {
    key: 'reservation_time_slot_increment_minutes',
    label: 'Time slot increment (minutes)',
    type: 'number',
    helper: '15, 30 or 60 typical.',
  },
  {
    key: 'reservation_default_duration_minutes',
    label: 'Default reservation duration (minutes)',
    type: 'number',
    helper:
      'Used by the table-conflict check. Override per reservation if needed.',
  },
  {
    key: 'reservation_notification_email',
    label: 'Admin notification email',
    type: 'email',
    helper: 'Where new request notifications go (Phase B).',
  },
];

export const ReservationSettingsValuesTab: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [rows, setRows] = React.useState<Setting[]>([]);
  const [values, setValues] = React.useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = React.useState<string | null>(null);

  React.useEffect(() => {
    SettingsService.getSettings()
      .then((all) => {
        setRows(all || []);
        const map: { [key: string]: string } = {};
        for (const row of all || []) {
          map[row.key] = row.value;
        }
        setValues(map);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const settingForKey = (key: string) => rows.find((s) => s.key === key);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const f of FIELDS) {
        const row = settingForKey(f.key);
        if (!row) continue;
        if (values[f.key] !== row.value) {
          await SettingsService.editSetting(row.id, { value: values[f.key] });
        }
      }
      setSnackbar('Saved');
    } catch (e) {
      setSnackbar((e as Error)?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      {loading && <DelayedLinearProgress />}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2}>
          {FIELDS.map((f) => {
            const exists = !!settingForKey(f.key);
            return (
              <Grid item xs={12} sm={6} key={f.key}>
                <TextField
                  label={f.label}
                  type={f.type}
                  value={values[f.key] || ''}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                  }
                  helperText={
                    exists
                      ? f.helper
                      : `Missing row in settings table: ${f.key}`
                  }
                  error={!exists}
                  fullWidth
                />
              </Grid>
            );
          })}
        </Grid>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          style={{ marginTop: 16 }}
        >
          Save
        </Button>
      </Paper>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </React.Fragment>
  );
};
